import React, { useCallback, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { Box, Text } from '@atlaskit/primitives';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { N200 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { useActionsCommandPalette } from '@atlassian/jira-command-palette';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { isMacOs } from '@atlassian/jira-common-components-keyboard-shortcuts-dialog/src/utils';
import { ff } from '@atlassian/jira-feature-flagging';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog';
import { DIALOG_WIDTH } from '../../common/constants';
import { useShortcutsActions } from '../../controllers';
import messages from './messages';
import type { Props } from './types';
import { useIsEmbed } from './utils';

const CommandPaletteSectionMessage = () => {
	const { formatMessage } = useIntl();
	const isEmbed = ff('compal-sunset-and-remove-dot-menu_66j8q')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsEmbed()
		: false;
	const { open } = ff('compal-dot-menu-sunset-announcement_lvchn')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useActionsCommandPalette()
		: {
				open: noop,
			};
	const [, { setShortcutsDialogVisibility }] = ff('compal-dot-menu-sunset-announcement_lvchn')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useShortcutsActions()
		: [
				undefined,
				{
					setShortcutsDialogVisibility: noop,
				},
			];

	const onClick = useCallback(() => {
		setShortcutsDialogVisibility(false);
		open();
	}, [open, setShortcutsDialogVisibility]);

	const strong = useCallback(
		(...chunks: React.ReactNode[]) => <Text as="strong">{chunks}</Text>,
		[],
	);

	return (
		<SectionMessage
			data-testid="shortcuts-dialog.ui.modal.section-message"
			appearance={
				// eslint-disable-next-line no-nested-ternary
				isEmbed
					? 'information'
					: ff('compal-dot-menu-sunset-announcement_lvchn') &&
						  !ff('compal-sunset-and-remove-dot-menu_66j8q')
						? 'warning'
						: 'discovery'
			}
			title={
				// eslint-disable-next-line no-nested-ternary
				isEmbed
					? undefined
					: // eslint-disable-next-line no-nested-ternary
						ff('compal-sunset-and-remove-dot-menu_66j8q')
						? formatMessage(messages.dotMenuRemovalTitle)
						: ff('compal-dot-menu-sunset-announcement_lvchn')
							? formatMessage(messages.sunsetDotMenuWarningTitle)
							: formatMessage(messages.commandPaletteFooterTitle, {
									isMac: isMacOs(),
								})
			}
			{...(ff('compal-dot-menu-sunset-announcement_lvchn')
				? {
						actions: [
							...(!isEmbed
								? [
										<SectionMessageAction key="try-now" onClick={onClick}>
											{formatMessage(messages.tryNowButton)}
										</SectionMessageAction>,
									]
								: []),
							<SectionMessageAction
								key="learn-more"
								href="https://community.atlassian.com/t5/Jira-Command-Palette-Beta-Group/We-re-removing-Jira-s-dot-menu-to-make-way-for-a-new-and/ba-p/2754322"
							>
								{formatMessage(messages.learnMoreButton)}
							</SectionMessageAction>,
						],
					}
				: {})}
		>
			{/* eslint-disable-next-line no-nested-ternary */}
			{ff('compal-sunset-and-remove-dot-menu_66j8q') ? (
				formatMessage(messages.dotMenuRemovalDescription, {
					strong,
					isMac: isMacOs(),
					isEmbed,
				})
			) : ff('compal-dot-menu-sunset-announcement_lvchn') ? (
				formatMessage(messages.sunsetDotMenuWarningDescription, {
					strong,
					isMac: isMacOs(),
				})
			) : (
				<FormattedMessage {...messages.commandPaletteFooterDescription} />
			)}
		</SectionMessage>
	);
};

const ModalFooterContainer = ({ children }: { children: ReactNode }) =>
	ff('compal-dot-menu-sunset-announcement_lvchn') ||
	ff('compal-sunset-and-remove-dot-menu_66j8q') ? (
		<Box padding="space.200">{children}</Box>
	) : (
		<ModalFooterContainerOld>
			<CommandPaletteFooter>{children}</CommandPaletteFooter>
		</ModalFooterContainerOld>
	);

const ModalFooterWrapper = () => (
	<ModalFooterContainer>
		<CommandPaletteSectionMessage />
	</ModalFooterContainer>
);

const Modal = (props: Props) => {
	const { children, onClose } = props;

	return (
		<ShortcutScope>
			<ModalDialog width={`${DIALOG_WIDTH}px`} onClose={onClose}>
				{!ff('compal-sunset-and-remove-dot-menu_66j8q') ? (
					<ModalBodyContainer>{children}</ModalBodyContainer>
				) : null}
				<ModalFooterWrapper />
			</ModalDialog>
		</ShortcutScope>
	);
};

export default Modal;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalBodyContainer = styled.div({
	overflow: 'hidden',
	maxHeight: '368px',
	padding: `${token('space.100', '8px')} ${token('space.100', '8px')} 0`,
	display: 'flex',
	flexDirection: 'column',
	boxSizing: 'border-box',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalFooterContainerOld = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > div': {
		padding: token('space.250', '20px'),
		font: token('font.body.UNSAFE_small'),
		color: token('color.text.subtlest', N200),
		justifyContent: 'space-between',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommandPaletteFooter = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	padding: `${token('space.100', '8px')} !important`,
	font: token('font.body'),
});
